export const get = (obj: any, path: string | number | any[], defaultValue: any = null): any => {
  if (path === null || path === undefined || obj === null || obj === undefined) {
    return defaultValue;
  }

  let fullPath;
  let result = obj;

  if (Array.isArray(path)) {
    fullPath = path.join('.');
  } else if (typeof path === 'number') {
    fullPath = path.toString();
  } else {
    fullPath = path;
  }

  const keys = fullPath?.split('.');

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (
      result === null ||
      result === undefined ||
      result[key] === undefined ||
      (!result[key] && result[key] !== 0 && result[key] !== false)
    ) {
      return defaultValue;
    }
    result = result[key];
  }
  return result;
};

export const set = (obj: any, path: string | number | any[], value: any): any => {
  if (path === null || path === undefined || obj === null || obj === undefined || !obj) {
    return obj;
  }

  let fullPath;
  let result = obj;

  if (Array.isArray(path)) {
    fullPath = path.join('.');
  } else if (typeof path === 'number') {
    fullPath = path.toString();
  } else {
    fullPath = path;
  }

  const keys = fullPath.split('.');

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (result[key] === undefined) {
      result[key] = {};
    }
    result = result[key];
  }

  result[keys[keys.length - 1]] = value;

  return obj;
};

export const debounce = (func: any, wait: number, immediate?: boolean) => {
  let timeout: any;
  return function (this: any) {
    const context: any = this;
    const args = arguments;
    const later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
};

export const isEqual = (value: any, other: any): boolean => {
  if (value === other) {
    return true;
  }
  if (value instanceof Date && other instanceof Date) {
    return value.getTime() === other.getTime();
  }
  if (!value || !other || (typeof value !== 'object' && typeof other !== 'object')) {
    // eslint-disable-next-line no-self-compare
    return value !== value && other !== other;
  }
  if (value.prototype !== other.prototype) {
    return false;
  }
  const keys = Object.keys(value);
  if (keys.length !== Object.keys(other).length) {
    return false;
  }
  return keys.every((k) => isEqual(value[k], other[k]));
};

export const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined) {
    return true;
  }
  if (Array.isArray(value) || typeof value === 'string') {
    return !value.length;
  }
  if (typeof value === 'object') {
    return !Object.keys(value).length;
  }
  return false;
};

export const isObject = (value: any): boolean => {
  return !!(value && typeof value === 'object' && value.constructor === Object);
};

export const isNumber = (value: any): boolean => {
  return typeof value === 'number' && !isNaN(value);
};

export const truncate = (str: string, options: any = {}): string => {
  if (!str) return '';

  const { length = 30, omission = '...', separator } = options;

  if (str.length <= length) {
    return str;
  }

  const end = separator ? str.lastIndexOf(separator, length) : length;

  if (end === -1) {
    return str.slice(0, length) + omission;
  }

  return str.slice(0, end) + omission;
};

export const capitalize = (str: string): string => {
  return (str || '').toString().charAt(0).toUpperCase() + str.slice(1);
};

export const deepClone = <T = any>(obj: any): T => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  const copy = obj.constructor();
  for (const attr in obj) {
    if (obj.hasOwnProperty(attr)) {
      copy[attr] = deepClone(obj[attr]);
    }
  }
  return copy;
};

export const parseStringToNumber = (value: string) => {
  const parsedValue = Number(value);

  if (isNaN(parsedValue)) {
    return value;
  }

  return parsedValue;
};

// removes html tags from string
export const stripHtml = (html: string) => {
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// function to check wheter the string is a valid url
export const isUrl = (str: string) => {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i',
  ); // fragment locator
  return !!pattern.test(str);
};

export const findKeyByValue = <T extends Record<string, any>>(object: T, value: T[keyof T]) => {
  return Object.keys(object).find((key) => object[key] === value);
};

export const getFullName = (object: any, defaultValue: any = '') => {
  if (object === null || object === undefined || Object.keys(object).length === 0) {
    return defaultValue;
  } else {
    return `${get(object, 'first_name')} ${get(object, 'last_name')} ${get(object, 'middle_name')}`;
  }
};
